import React, { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { getProjectSites, updateProjectSiteStatus } from 'services/clients.svc'
import { PageTitle } from 'context/PageTitleContext'
import { AddIcon, RightArrowIcon } from 'components/Icons'
import { EmptyList } from 'components'
import { useTableActions } from 'hooks/useTableActions'
import { mediumTime } from 'common/constants'
import { toast } from 'react-toastify'
import { siteStatus, nextStatusOptions } from './constants'
import ConfirmModal from 'pages/company/SwmsManagement/ConfirmModal'
import './ProjectSites.scss'

function groupByMonth(list) {
  const groups = {}
  list.forEach((site) => {
    const d = new Date(site.createdAt)
    const label = isNaN(d.getTime())
      ? 'Unknown'
      : d.toLocaleString('en-AU', { month: 'long', year: 'numeric' })
    if (!groups[label]) groups[label] = []
    groups[label].push(site)
  })
  const labels = Object.keys(groups).sort(
    (a, b) => new Date(groups[b][0].createdAt) - new Date(groups[a][0].createdAt),
  )
  return labels.map((label) => ({ label, items: groups[label] }))
}

function StatusCell({ site, onChangeStatus, disabled }) {
  const options = nextStatusOptions(site.status)
  return (
    <div className="status-cell">
      <div className="status-row">
        <span className={`status-tag ${site.status}`}>
          {siteStatus[site.status]}
        </span>
        {options.length > 0 && (
          <div className="status-links">
            {options.map((opt, i) => (
              <React.Fragment key={opt.value}>
                {i > 0 && <span className="sep">|</span>}
                <button
                  type="button"
                  className="status-link"
                  disabled={disabled}
                  onClick={() => onChangeStatus(site._id, opt.value, site.address)}
                >
                  {opt.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {site.statusChangedAt && site.statusChangedByDetails && (
        <span className="status-date">
          &#10003; {mediumTime.format(new Date(site.statusChangedAt))}
          <span className="status-by">
            {' '}· {site.statusChangedByDetails.firstName} {site.statusChangedByDetails.surname}
          </span>
        </span>
      )}
    </div>
  )
}

const STATUS_CHIPS = [
  { key: 'ongoing',   label: 'On Going',  cls: 'chip-ongoing'   },
  { key: 'on_hold',   label: 'On Hold',   cls: 'chip-on-hold'   },
  { key: 'completed', label: 'Completed', cls: 'chip-completed'  },
]

function SiteGroup({ label, items, onChangeStatus, mutationLoading }) {
  const [open, setOpen] = useState(true)

  const statusCounts = STATUS_CHIPS.reduce((acc, { key }) => {
    acc[key] = items.filter((s) => s.status === key).length
    return acc
  }, {})

  return (
    <div className="month-group">
      <div className="month-header" onClick={() => setOpen((v) => !v)}>
        <span className="chevron">{open ? '▼' : '▶'}</span>
        <span>{label}</span>
        <div className="header-status-chips">
          {STATUS_CHIPS.filter(({ key }) => statusCounts[key] > 0).map(({ key, label: chipLabel, cls }) => (
            <span key={key} className={`header-chip ${cls}`}>
              {chipLabel} <span className="chip-count">{statusCounts[key]}</span>
            </span>
          ))}
        </div>
      </div>
      {open && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Street Address</th>
                <th>Suburb</th>
                <th>Site Supervisor</th>
                <th>Client</th>
                <th>Job No.</th>
                <th>Created</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((site) => (
                <tr key={site._id}>
                  <td>{site.address}</td>
                  <td>{site.suburb}</td>
                  <td>{site.supervisorName}</td>
                  <td>{site.clientName}</td>
                  <td>{site.siteNumber}</td>
                  <td className="created-col">{mediumTime.format(new Date(site.createdAt))}</td>
                  <td>
                    <StatusCell
                      site={site}
                      onChangeStatus={onChangeStatus}
                      disabled={mutationLoading}
                    />
                  </td>
                  <td className="arrow-col">
                    <Link to={`${site._id}`}>
                      <RightArrowIcon fill={'var(--font-color)'} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function ProjectSites() {
  const { companyId } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useQuery(['projectSites'], () =>
    getProjectSites(companyId),
  )

  const {
    list,
    tableActions: { search },
    setSearch,
  } = useTableActions(data)

  const [statusFilter, setStatusFilter] = useState(null)
  const [pendingComplete, setPendingComplete] = useState(null)

  const counts = useMemo(
    () => ({
      all: list.length,
      ongoing: list.filter((s) => s.status === 'ongoing').length,
      on_hold: list.filter((s) => s.status === 'on_hold').length,
      completed: list.filter((s) => s.status === 'completed').length,
    }),
    [list],
  )

  const displayed = statusFilter ? list.filter((s) => s.status === statusFilter) : list
  const groups = useMemo(() => groupByMonth(displayed), [displayed])

  const updateStatusMutation = useMutation(
    ({ projectSiteId, selectedStatus }) =>
      updateProjectSiteStatus({
        companyId,
        data: { projectSiteIds: [projectSiteId], selectedStatus },
      }),
    {
      onSuccess: (_, { address, selectedStatus }) => {
        queryClient.invalidateQueries(['projectSites'])
        toast.success(`${address} marked as ${siteStatus[selectedStatus]}`)
      },
      onError: (err, { address }) => {
        toast.error(`Failed to update status for ${address}`)
        console.error('Failed to update', err)
      },
    },
  )

  const handleChangeStatus = (projectSiteId, selectedStatus, address) => {
    if (selectedStatus === 'completed') {
      setPendingComplete({ projectSiteId, address })
      return
    }
    updateStatusMutation.mutate({ projectSiteId, selectedStatus, address })
  }

  const handleConfirmComplete = () => {
    updateStatusMutation.mutate({
      projectSiteId: pendingComplete.projectSiteId,
      selectedStatus: 'completed',
      address: pendingComplete.address,
    })
    setPendingComplete(null)
  }

  const statusTabs = [
    { key: null, label: 'All', count: counts.all },
    { key: 'ongoing', label: 'On Going', count: counts.ongoing },
    { key: 'on_hold', label: 'On Hold', count: counts.on_hold },
    { key: 'completed', label: 'Completed', count: counts.completed },
  ]

  return (
    <div className="project-sites-container body-container">
      <PageTitle title="Projects" />

      <div className="page-header">
        <h2>Project Sites</h2>
        <div className="header-actions">
          <Link to="add" role="button" className="outline">
            <AddIcon fill={'var(--color-black)'} />
            Create New Site Project
          </Link>
          <input
            type="text"
            className="search-input"
            placeholder="Search anything..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="filter-bar">
        {statusTabs.map((tab) => (
          <button
            key={String(tab.key)}
            type="button"
            className={`filter-tab${statusFilter === tab.key ? ' active' : ''}`}
            onClick={() => setStatusFilter(tab.key)}
          >
            {tab.label} <span className="tab-count">({tab.count})</span>
          </button>
        ))}
      </div>

      {isLoading && <p className="page-notice">Loading…</p>}
      {isError && <p className="page-notice error">Failed to load projects. Please try again.</p>}
      {!isLoading && !isError && displayed.length === 0 && <EmptyList />}

      {!isLoading &&
        !isError &&
        groups.map(({ label, items }) => (
          <SiteGroup
            key={label}
            label={label}
            items={items}
            onChangeStatus={handleChangeStatus}
            mutationLoading={updateStatusMutation.isLoading}
          />
        ))}

      <ConfirmModal
        confirm={
          pendingComplete
            ? {
                type: 'retire',
                title: 'Mark Site as Completed?',
                label: pendingComplete.address,
                message:
                  'Please confirm that all site works are finished, all safety obligations have been met, and the site is ready to be closed.',
                warningText:
                  'Once marked as Completed, this project site cannot be changed back. All active SWMS assignments for this site will be archived.',
                okLabel: 'Mark Complete',
              }
            : null
        }
        onConfirm={handleConfirmComplete}
        onCancel={() => setPendingComplete(null)}
      />
    </div>
  )
}
